import React, { useMemo } from 'react';
import { Certificate } from '@phosphor-icons/react';

interface CertRow {
    certType: string;
    certCount: number;
}

interface OrgCertificateIssuanceWidgetProps {
    data?: CertRow[] | null;
    loading?: boolean;
    error?: string;
}

export const OrgCertificateIssuanceWidget: React.FC<OrgCertificateIssuanceWidgetProps> = ({ data, loading, error }) => {
    const rows = Array.isArray(data) ? data : [];

    const { total, completion, attendance, digitalBadge } = useMemo(() => {
        let completion = 0, attendance = 0, digitalBadge = 0;
        rows.forEach(r => {
            const count = Number(r.certCount) || 0;
            if (r.certType === 'Completion') completion = count;
            else if (r.certType === 'Attendance') attendance = count;
            else if (r.certType === 'Digital Badge') digitalBadge = count;
        });
        const total = completion + attendance + digitalBadge;
        return { total, completion, attendance, digitalBadge };
    }, [rows]);

    return (
        <div className="flex flex-col h-full p-5">
            <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400">Organization-wide</span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : rows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Certificate className="w-8 h-8" />
                        <span className="text-sm">No certificate data</span>
                    </div>
                ) : (
                    <>
                        {/* Total Issued */}
                        <div className="text-center mb-6">
                            <div className="text-5xl font-bold text-emerald-600">{total}</div>
                            <div className="text-sm text-gray-400 mt-1">Total Issued</div>
                        </div>

                        {/* Type Breakdown */}
                        <div className="grid grid-cols-3 gap-3 w-full">
                            <div className="text-center p-3 bg-emerald-50 rounded-lg">
                                <div className="text-2xl font-bold text-emerald-600">{completion}</div>
                                <div className="text-[10px] text-gray-500 mt-0.5">Completion</div>
                            </div>
                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                                <div className="text-2xl font-bold text-purple-600">{attendance}</div>
                                <div className="text-[10px] text-gray-500 mt-0.5">Attendance</div>
                            </div>
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{digitalBadge}</div>
                                <div className="text-[10px] text-gray-500 mt-0.5">Digital Badge</div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
